import React, { useEffect } from 'react';
import { useIntl } from '@leagueplatform/locales';
import { Button } from '@leagueplatform/genesis-core';
import { ConfirmationModal } from '#components/confirmation-modal/confirmation-modal.component.tsx';
import { track } from '@leagueplatform/analytics';
import { useParamsLegacy as useParams } from '@leagueplatform/routing';
import { useGetChallengeByIdData } from '#pages/challenge-landing-page/hooks/use-get-challenge-by-id-data.hook.ts';
import { CHALLENGE_CAMPAIGN_TYPE } from '#constants/analytics.constants.tsx';
import { useLeaveChallenge } from './hooks/use-leave-challenge.hook';

export interface LeaveChallengeModalProps {
  isSettingsDropdownOpen: boolean;
  onDismiss: () => void;
  title: string;
}

export const LeaveChallengeModal = ({
  isSettingsDropdownOpen,
  onDismiss,
  title,
}: LeaveChallengeModalProps) => {
  const { formatMessage } = useIntl();
  const leaveChallengeMutation = useLeaveChallenge();
  const { challengeId } = useParams<{ challengeId: string }>();
  const { data: challengeData } = useGetChallengeByIdData();

  // Calling onDismiss on unmounting of this component, so that the leave challenge modal doesn't automatically open when clicking the settings cog after signing up for a challenge. This happened when a user joins a challenge, then leaves, then tries rejoining and leaving the challenge a second time.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => onDismiss, []);

  return (
    <ConfirmationModal
      heading={formatMessage({ id: 'LEAVE_QUESTION' }, { name: title })}
      description={formatMessage({
        id: 'LEAVE_CHALLENGE_DETAIL',
      })}
      isSettingsDropdownOpen={isSettingsDropdownOpen}
      onMountAnalytics={() =>
        track('challenges.screen_loaded.leave_challenge_confirmation_modal', {
          health_campaign_id: challengeId ?? '',
          health_campaign_name: challengeData?.data.title ?? '',
          health_campaign_type: CHALLENGE_CAMPAIGN_TYPE,
          challenge_status: challengeData?.data.status ?? '',
        })
      }
      onDismiss={() => {
        onDismiss();
        track(
          'challenges.button_clicked.leave_challenge_confirmation_modal.exit',
          {
            health_campaign_id: challengeId ?? '',
            health_campaign_name: challengeData?.data.title ?? '',
            health_campaign_type: CHALLENGE_CAMPAIGN_TYPE,
            challenge_status: challengeData?.data.status ?? '',
          },
        );
      }}
    >
      <Button
        destructive
        loading={leaveChallengeMutation.isLoading}
        onClick={() => {
          leaveChallengeMutation.mutate();
          track(
            'challenges.button_clicked.leave_challenge_confirmation_modal.confirm_leave_challenge',
            {
              health_campaign_id: challengeId ?? '',
              health_campaign_name: challengeData?.data.title ?? '',
              health_campaign_type: CHALLENGE_CAMPAIGN_TYPE,
              challenge_status: challengeData?.data.status ?? '',
            },
          );
        }}
      >
        {formatMessage({ id: 'LEAVE' })}
      </Button>
    </ConfirmationModal>
  );
};
